import React, { useCallback, useEffect, useRef, useState } from 'react';
import clsx from 'clsx';
import {
  faRectangleLandscape,
  faRectangleVertical,
  faSquare,
} from '@soundxyz/font-awesome/pro-solid-svg-icons/index.mjs';

export const ASPECT_RATIOS = [
  {
    label: 'portrait',
    description: 'Portrait image (1080 x 1920px) to share as story on Tiktok and Instagram.',
    icon: faRectangleVertical,
    width: 1080,
    height: 1920,
  },
  {
    label: 'landscape',
    description: 'Landscape image (1024 x 512px) to share as post on Twitter and Telegram.',
    icon: faRectangleLandscape,
    width: 1024,
    height: 512,
  },
  {
    label: 'square',
    description: 'Square image (1080 x 1080px) to share on as post on Instagram and Facebook.',
    icon: faSquare,
    width: 1080,
    height: 1080,
  },
  {
    label: 'square-small',
    description: 'Small Square image (552 x 552px) to share on as post on Instagram and Facebook.',
    icon: faSquare,
    width: 552,
    height: 552,
  },
] as const;

type ScreenshotContainerProps = {
  /**
   * Ref to the element to be rendered as screenshot
   */
  screenshotRef: React.RefObject<HTMLDivElement>;
  /**
   * Selected aspect ratio
   */
  selectedAspectRatio: (typeof ASPECT_RATIOS)[number];
  /**
   * Class name to be applied to the container
   */
  className?: string;
};

export const ScreenshotContainer = ({
  screenshotRef,
  selectedAspectRatio,
  className,
  children,
}: React.PropsWithChildren<ScreenshotContainerProps>) => {
  const availableSpaceRef = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(1);

  const getScale = useCallback(() => {
    if (screenshotRef.current === null) {
      return 1;
    }

    return (availableSpaceRef.current?.clientWidth || 100) / selectedAspectRatio.width;
  }, [screenshotRef, selectedAspectRatio.width]);

  useEffect(() => {
    setScale(getScale());
  }, [screenshotRef.current?.clientWidth, availableSpaceRef.current?.clientWidth, getScale]);

  return (
    <div
      className={clsx('flex w-full items-center justify-center overflow-hidden', className)}
      style={{
        aspectRatio: `${selectedAspectRatio.width}/${selectedAspectRatio.height}`,
      }}
      ref={availableSpaceRef}
    >
      <div
        className="flex flex-shrink-0 overflow-hidden"
        style={{
          transform:
            // Calculate scale to fit the desired aspect ratio
            `scale(${scale})`,
          width: `${selectedAspectRatio.width}px`,
          height: `${selectedAspectRatio.height}px`,
        }}
      >
        <div
          className="flex h-full w-full items-center justify-center overflow-hidden"
          ref={screenshotRef}
        >
          {children}
        </div>
      </div>
    </div>
  );
};
